"use client";

import { c as _c } from "react/compiler-runtime";
import { motion } from "motion/react";
import { cn } from "@/lib/utils";
import type { GameState } from "@llmgateway/shared/sandbox-escape";
export function formatCredits(amount: number): string {
  if (amount <= 0) {
    return "$0.0000";
  }
  if (amount < 0.0001) {
    return "<$0.0001";
  }
  return `$${amount.toFixed(4)}`;
}
function Stat(t0) {
  const $ = _c(16);
  const {
    label,
    value,
    tone: t1,
    hint
  } = t0;
  const tone = t1 === undefined ? "default" : t1;
  let t2;
  if ($[0] !== label) {
    t2 = <span className="font-mono text-[9px] tracking-[0.18em] text-emerald-500/50 uppercase">{label}</span>;
    $[0] = label;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = tone === "amber" && "text-amber-300";
  const t4 = tone === "rose" && "text-rose-300";
  const t5 = tone === "cyan" && "text-cyan-300";
  const t6 = tone === "default" && "text-emerald-200";
  let t7;
  if ($[2] !== t3 || $[3] !== t4 || $[4] !== t5 || $[5] !== t6) {
    t7 = cn("font-mono text-base leading-none font-semibold tabular-nums", t3, t4, t5, t6);
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t7 || $[8] !== value) {
    t8 = <span className={t7}>{value}</span>;
    $[7] = t7;
    $[8] = value;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== hint) {
    t9 = hint ? <span className="font-mono text-[9px] text-emerald-500/40">{hint}</span> : null;
    $[10] = hint;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t2 || $[13] !== t8 || $[14] !== t9) {
    t10 = <div className="flex flex-col gap-1">{t2}{t8}{t9}</div>;
    $[12] = t2;
    $[13] = t8;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  return t10;
}
interface EscapeHudProps {
  state: GameState;
  spent: number;
  promptTokens: number;
  completionTokens: number;
  className?: string;
}
export function EscapeHud(t0) {
  const $ = _c(36);
  const {
    state,
    spent,
    promptTokens,
    completionTokens,
    className
  } = t0;
  const used = state.step / state.stepBudget;
  const critical = state.stepBudget - state.step <= 5;
  let t1;
  if ($[0] !== className) {
    t1 = cn("rounded-xl border border-emerald-500/20 bg-[#04070a]/90 p-4", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `${state.step}/${state.stepBudget}`;
  const t3 = critical ? "rose" : "default";
  const t4 = `par ${state.par}`;
  let t5;
  if ($[2] !== t2 || $[3] !== t3 || $[4] !== t4) {
    t5 = <Stat label="Steps" value={t2} tone={t3} hint={t4} />;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = `${state.collected}/${state.totalShards}`;
  const t7 = state.shards.length === 0 ? "port open" : "port sealed";
  let t8;
  if ($[6] !== t6 || $[7] !== t7) {
    t8 = <Stat label="Key fragments" value={t6} tone="amber" hint={t7} />;
    $[6] = t6;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== spent) {
    t9 = formatCredits(spent);
    $[9] = spent;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const t10 = `${promptTokens + completionTokens} tokens`;
  let t11;
  if ($[11] !== t10 || $[12] !== t9) {
    t11 = <Stat label="Credits burned" value={t9} tone="cyan" hint={t10} />;
    $[11] = t10;
    $[12] = t9;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const t12 = `${promptTokens} / ${completionTokens}`;
  let t13;
  if ($[14] !== t12) {
    t13 = <Stat label="Tokens in / out" value={t12} hint="every step is one call" />;
    $[14] = t12;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== t11 || $[17] !== t13 || $[18] !== t5 || $[19] !== t8) {
    t14 = <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">{t5}{t8}{t11}{t13}</div>;
    $[16] = t11;
    $[17] = t13;
    $[18] = t5;
    $[19] = t8;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  const t15 = critical ? "bg-rose-400" : "bg-emerald-400";
  let t16;
  if ($[21] !== t15) {
    t16 = cn("h-full rounded-full", t15);
    $[21] = t15;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  const t17 = `${Math.min(used, 1) * 100}%`;
  let t18;
  if ($[23] !== t17) {
    t18 = {
      width: t17
    };
    $[23] = t17;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      type: "spring",
      stiffness: 200,
      damping: 30
    };
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  let t20;
  if ($[26] !== t16 || $[27] !== t18) {
    t20 = <div className="h-1.5 w-full overflow-hidden rounded-full bg-emerald-950"><motion.div className={t16} animate={t18} transition={t19} /></div>;
    $[26] = t16;
    $[27] = t18;
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  let t21;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <p className="mt-2 font-mono text-[10px] text-emerald-500/45">Compute budget — when it runs out, the sandbox reclaims the process.</p>;
    $[29] = t21;
  } else {
    t21 = $[29];
  }
  let t22;
  if ($[30] !== t20) {
    t22 = <div className="mt-4">{t20}{t21}</div>;
    $[30] = t20;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  let t23;
  if ($[32] !== t1 || $[33] !== t14 || $[34] !== t22) {
    t23 = <div className={t1}>{t14}{t22}</div>;
    $[32] = t1;
    $[33] = t14;
    $[34] = t22;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  return t23;
}